import { useCallback, useMemo } from 'react';
import { Container, Group, Stack, createStyles, useMantineTheme, Image } from '@mantine/core';
import type { ZodTypeAny } from 'zod';
import { z } from 'zod';
import type { FileWithPath } from '@mantine/dropzone';
import { Dropzone } from '@mantine/dropzone';
import { IconPhoto, IconUpload, IconX } from '@tabler/icons-react';

import { Text } from '~/domains/common/components/Text/Text';
import { CommissionerAssetType } from '~/domains/commissioner/interfaces/channel.interface';
import ImageUploadRules from '~/domains/commissioner/components/inputs/ImageUpload/ImageUploadRules';
import dt from '~/domains/contest/domains/managev2/domains/common/testing.const';

import type { BaseInterativeField, FieldDescriptor, StaticFieldProps } from '../../types/common';
import { FieldMeta } from '../common/FieldMeta';

const keyToAssetTypeMap = {
  banner: CommissionerAssetType.BACKDROP,
  avatar: CommissionerAssetType.AVATAR,
};

const assetConfig = {
  [CommissionerAssetType.AVATAR]: {
    mediaPath: 'commissioner/avatars',
    fileSizeLimit: {
      min: 0,
      max: 1 * 1024 * 1024, // 1 MB in bytes
    },
    fileDimensionLimit: {
      width: {
        min: null,
        max: null,
      },
      height: {
        min: null,
        max: null,
      },
      ratio: {
        min: 0.8,
        max: 1.2,
      },
    },
    suggestedDimension: {
      width: 256,
      height: 256,
    },

    mimeTypes: ['image/jpeg', 'image/png'],
    formats: ['png', 'jpg', 'jpeg'],
  },
  [CommissionerAssetType.BACKDROP]: {
    mediaPath: 'commissioner/backdrops',
    fileSizeLimit: {
      min: 0,
      max: 4 * 1024 * 1024, // 4 MB in bytes
    },
    fileDimensionLimit: {
      width: {
        min: null,
        max: null,
      },
      height: {
        min: null,
        max: null,
      },
      ratio: {
        min: 1.2,
        max: 2,
      },
    },
    suggestedDimension: {
      width: 2880,
      height: 170,
    },

    mimeTypes: ['image/jpeg', 'image/png'],
    formats: ['png', 'jpg', 'jpeg'],
  },
};

const useStyles = createStyles(() => ({
  dropzoneGroup: { minHeight: 220, pointerEvents: 'none' },
}));

export type ImageFieldType = BaseInterativeField & {
  type: 'ImageField';
};

function ImageFieldElement({
  field,
  value,
  onValueChange,
  errors,
  isTouched,
}: StaticFieldProps<ImageFieldType>) {
  const theme = useMantineTheme();
  const { classes } = useStyles();
  const assetTypeKey = keyToAssetTypeMap[field.key];
  const assetType = assetConfig[assetTypeKey];
  const { fileSizeLimit, mimeTypes } = assetType;
  const handleOnChange = useCallback(
    (newValue: File) => {
      onValueChange(newValue);
    },
    [onValueChange]
  );
  const imageUrl = useMemo(() => {
    if (!value || !(value instanceof File)) {
      return;
    }
    return URL.createObjectURL(value);
  }, [value]);

  const handleDrop = useCallback(
    (files: FileWithPath[]) => {
      handleOnChange(files[0]);
    },
    [handleOnChange]
  );

  return (
    <div>
      <FieldMeta field={field} errors={errors} isTouched={isTouched} />
      <Dropzone onDrop={handleDrop} maxFiles={1} maxSize={fileSizeLimit.max} accept={mimeTypes}>
        <Group
          position="center"
          spacing="xl"
          className={classes.dropzoneGroup}
          data-test-id={dt.image.container}
        >
          {imageUrl ? (
            <Stack>
              <Text mt={8} size="xl" inline align="center">
                Drag or click here to replace this image
              </Text>
              <Container>
                <Image maw={100} src={imageUrl} alt="uploaded image" />
              </Container>
              <ImageUploadRules assetType={assetTypeKey} />
            </Stack>
          ) : (
            <>
              <Dropzone.Accept>
                <IconUpload
                  size={50}
                  data-test-id={dt.image.success}
                  stroke={1.5}
                  color={theme.colors[theme.primaryColor][theme.colorScheme === 'dark' ? 4 : 6]}
                />
              </Dropzone.Accept>
              <Dropzone.Reject>
                <IconX
                  size={50}
                  data-test-id={dt.image.reject}
                  stroke={1.5}
                  color={theme.colors.red[theme.colorScheme === 'dark' ? 4 : 6]}
                />
              </Dropzone.Reject>
              <Dropzone.Idle>
                <IconPhoto data-test-id={dt.image.idle} size={50} stroke={1.5} />
              </Dropzone.Idle>
              <div data-test-id={dt.image.drag}>
                <Text variant="web-body-large">Drag image here or click to select one</Text>
                <ImageUploadRules assetType={assetTypeKey} />
              </div>
            </>
          )}
        </Group>
      </Dropzone>
    </div>
  );
}

const createValidationSchema = (field: ImageFieldType) => {
  let fieldSchema: ZodTypeAny = z.instanceof(File);
  if (field.validation?.isOptional) {
    fieldSchema = fieldSchema.optional();
  }

  return fieldSchema;
};

const Descriptor: FieldDescriptor<ImageFieldType> = {
  FieldComponent: ImageFieldElement,
  name: 'ImageField',
  createValidationSchema,
};

export default Descriptor;
